import { Accomodation, MapState } from './types'
import * as Zoom from './zoom'
import * as Api from './api'
import * as Immutable from 'immutable'
import * as Loadjs from 'loadjs'

export const getBoundingRegionForCluster = (cluster: Accomodation) => {
  const region = new mapkit.BoundingRegion(
    parseFloat(cluster.max.lat),
    parseFloat(cluster.max.lng),
    parseFloat(cluster.min.lat),
    parseFloat(cluster.min.lng)
  ).toCoordinateRegion()

  // Add some margin so all the clusters are clearly visible
  region.span = new mapkit.CoordinateSpan(
    region.span.latitudeDelta * 1.5,
    region.span.longitudeDelta * 1.5
  )

  return region;
}

let mapKitInitialized = false;

export const initialize = () : Promise<void> => {
  if (mapKitInitialized) {
    return;
  }
  mapKitInitialized = true;
  return new Promise<void>((resolve) => {
    Loadjs(['https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js'], () => {
      mapkit.init({
        authorizationCallback: Api.authorizationCallback
      })
      resolve()
    })
  })
}

// Compatibility with google maps bounds.toUrlValue()
export const boundsToGMString = (boundingRegion : mapkit.BoundingRegion) => {
  return [
    parseFloat(boundingRegion.southLatitude.toFixed(5)),
    parseFloat(boundingRegion.westLongitude.toFixed(5)),
    parseFloat(boundingRegion.northLatitude.toFixed(5)),
    parseFloat(boundingRegion.eastLongitude.toFixed(5)),
  ].join(',');
}

export const mapStateToUrlParam = (map_state: MapState) : string => {
  if (!map_state) {
    return null;
  }
  return [map_state.lat, map_state.lng, map_state.zoom].join('_')
}

export const mapStateFromUrlParam = (value: string) : MapState => {
  let values : number[] = value.split('_').map(v => parseFloat(v))
  if (values.length !== 3) {
    return null;
  }

  return {
    lat: values[0],
    lng: values[1],
    zoom: values[2],
  }
}

const roundMapState = (map_state: MapState, fractionDigits: number = 6) : MapState => {
  if (!map_state) {
    return null;
  }
  return {
    lat: parseFloat(map_state.lat.toFixed(fractionDigits)),
    lng: parseFloat(map_state.lng.toFixed(fractionDigits)),
    zoom: map_state.zoom,
  }
}

export const mapStateEqual = (ms1:MapState, ms2:MapState) : boolean => {
  return mapStateToUrlParam(roundMapState(ms1)) == mapStateToUrlParam(roundMapState(ms2));
}

export const mapStateToCoordinateRegion = (map: mapkit.Map, map_state:MapState) : mapkit.CoordinateRegion => {

  const center = new mapkit.Coordinate(map_state.lat, map_state.lng);

  const span = Zoom.deltaFromZoomLevel(map, center, map_state.zoom - 1)

  return new mapkit.CoordinateRegion(
    center,
    span
  )
}

export const mapToMapState = (map:mapkit.Map) : MapState => {
  const region = map.region;
  return {
    lat: region.center.latitude,
    lng: region.center.longitude,
    zoom: parseInt(Zoom.getZoom(map).toFixed(0))
  }
}

type AnnotationDiff = {
  add: Immutable.List<mapkit.Annotation>,
  remove: Immutable.List<mapkit.Annotation>
}
export const annotationDiff = (
  currentAnnotations: Immutable.List<mapkit.Annotation>,
  newAnnotations: Immutable.List<mapkit.Annotation>,
) : AnnotationDiff => {
  const clusterOrNotContains = (annotation: mapkit.Annotation, collection: Immutable.List<mapkit.Annotation>) => {
    if (annotation.data.accommodation.type == 'cluster') {
      return true;
    }

    return !collection.find(acc => acc.data.accommodation.id == annotation.data.accommodation.id);
  }
  // We always want to remove current clusters, and we remove current annotations not in `newAnnotations`
  const remove = currentAnnotations.filter(annotation => clusterOrNotContains(annotation, newAnnotations))

  // We always want to add new clusters, and we want to add annotations not in `currentAnnotations`
  const add = newAnnotations.filter(annotation => clusterOrNotContains(annotation, currentAnnotations))

  return {
    'add': Immutable.List<mapkit.Annotation>(add),
    'remove': Immutable.List<mapkit.Annotation>(remove)
  }
}
